// File: frontend/src/components/common/PhoneInput.js

import React, { useState, useRef, useEffect } from 'react';
import styles from './PhoneInput.module.css';

const COUNTRIES = {
  'ca': { name: 'Canada',         code: '+1',  flag: '🇨🇦', maxLength: 10 },
  'us': { name: 'United States',  code: '+1',  flag: '🇺🇸', maxLength: 10 },
  'gb': { name: 'United Kingdom', code: '+44', flag: '🇬🇧', maxLength: 10 },
  'se': { name: 'Sweden',         code: '+46', flag: '🇸🇪', maxLength: 9  },
  'es': { name: 'Spain',          code: '+34', flag: '🇪🇸', maxLength: 9  }
};

const PhoneInput = ({
  value = { phoneNumber: '', fullNumber: '' },
  onChange,
  label,
  placeholder,
  required = false,
  error = null,
  disabled = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('ca');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null); // ✅ Track desired cursor position
  
  const country = COUNTRIES[selectedCountry];
  
  // ✅ Sync display value with prop value
  useEffect(() => {
    if (value.phoneNumber) {
      setDisplayValue(formatPhoneNumber(value.phoneNumber, selectedCountry));
    } else {
      setDisplayValue('');
    }
  }, [value.phoneNumber, selectedCountry]);
  
  // ✅ Set cursor position after render
  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      cursorPositionRef.current = null;
    }
  }, [displayValue]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handlePhoneNumberChange = (e) => {
    const input = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    // ✅ Get digits only
    const digitsOnly = input.replace(/\D/g, '');
    const validatedInput = digitsOnly.slice(0, country.maxLength);
    
    // ✅ Count how many digits are before cursor
    const inputBeforeCursor = input.slice(0, cursorPosition);
    const digitsBeforeCursor = inputBeforeCursor.replace(/\D/g, '').length;
    
    // ✅ Format the number
    const formatted = formatPhoneNumber(validatedInput, selectedCountry);
    
    // ✅ Calculate new cursor position based on digit count
    let newCursorPosition = 0;
    let digitCount = 0;
    
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitCount++;
        if (digitCount === digitsBeforeCursor) {
          newCursorPosition = i + 1;
          break;
        }
      }
    }
    
    // ✅ If we're at the end, place cursor at end
    if (digitCount < digitsBeforeCursor) {
      newCursorPosition = formatted.length;
    }
    
    // ✅ Store cursor position for useEffect
    cursorPositionRef.current = newCursorPosition;
    
    // ✅ Update display
    setDisplayValue(formatted);
    
    // ✅ Extract parts
    const areaCode = country.code === '+1' ? validatedInput.slice(0, 3) : '';
    const phoneLocal = country.code === '+1' ? validatedInput.slice(3, 10) : validatedInput;
    
    const newValue = {
      phoneNumber: validatedInput,
      fullNumber: `${country.code}${validatedInput}`,
      countryCode: country.code,
      areaCode: areaCode,
      localNumber: phoneLocal
    };

    if (onChange) {
      onChange(newValue);
    }
  };
  
  // ✅ Handle backspace specially
  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const cursorPosition = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      
      // If there's a selection, let default behavior handle it
      if (cursorPosition !== selectionEnd) {
        return;
      }
      
      const currentChar = displayValue[cursorPosition - 1];
      
      // If cursor is right after a formatting character, delete the digit before it
      if (currentChar === ')' || currentChar === ' ' || currentChar === '-') {
        e.preventDefault();
        
        const digitsOnly = displayValue.replace(/\D/g, '');
        const newDigits = digitsOnly.slice(0, -1);
        
        const formatted = formatPhoneNumber(newDigits, selectedCountry);
        setDisplayValue(formatted);
        
        // ✅ Calculate cursor position based on remaining digits
        const digitsBeforeCursor = displayValue.slice(0, cursorPosition - 1).replace(/\D/g, '').length - 1;
        
        let newCursorPosition = 0;
        let digitCount = 0;
        
        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) {
              newCursorPosition = i + 1;
              break;
            }
          }
        }
        
        if (digitCount < digitsBeforeCursor) {
          newCursorPosition = formatted.length;
        }
        
        cursorPositionRef.current = newCursorPosition;
        
        const areaCode = country.code === '+1' ? newDigits.slice(0, 3) : '';
        const phoneLocal = country.code === '+1' ? newDigits.slice(3, 10) : newDigits;
        
        const newValue = {
          phoneNumber: newDigits,
          fullNumber: `${country.code}${newDigits}`,
          countryCode: country.code,
          areaCode: areaCode,
          localNumber: phoneLocal
        };
        
        if (onChange) {
          onChange(newValue);
        }
      }
    }
  };

  const handleCountryChange = (countryKey) => {
    setSelectedCountry(countryKey);
    setIsDropdownOpen(false);
    setDisplayValue('');
    
    if (onChange) {
      onChange({
        phoneNumber: '',
        fullNumber: '',
        countryCode: COUNTRIES[countryKey].code,
        areaCode: '',
        localNumber: ''
      });
    }
  };
  
  const formatPhoneNumber = (number, countryKey) => {
    if (!number) return '';
    
    const digits = number.replace(/\D/g, '');
    const c = COUNTRIES[countryKey] || COUNTRIES['ca'];
    
    // North America +1 format: (XXX) XXX-XXXX
    if (c.code === '+1') {
      if (digits.length <= 3) {
        return digits;
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    }
    
    // International: no formatting, just digits
    // Clean display is fine — formatting varies too much by country
    return digits;
  };
  
  return (
    <div className={styles.phoneInputContainer}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={`${styles.inputGroup} ${error ? styles.hasError : ''} ${isFocused ? styles.focused : ''}`}>
        <div className={styles.countrySelector} ref={dropdownRef}>
          <button
            type="button"
            className={styles.countrySelectorButton}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
          >
            <span className={styles.flag}>{country.flag}</span>
            <span className={styles.countryCode}>{country.code}</span>
            <svg 
              width="12" 
              height="12" 
              viewBox="0 0 24 24" 
              fill="none"
              className={`${styles.chevron} ${isDropdownOpen ? styles.chevronOpen : ''}`}
            >
              <path 
                d="M6 9L12 15L18 9" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isDropdownOpen && (
            <div className={styles.countryDropdown}>
              {Object.entries(COUNTRIES).map(([key, { name, code, flag }]) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.countryOption} ${selectedCountry === key ? styles.countryOptionActive : ''}`}
                  onClick={() => handleCountryChange(key)}
                >
                  <span className={styles.flag}>{flag}</span>
                  <span className={styles.countryName}>{name}</span>
                  <span className={styles.countryCodeSmall}>{code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="tel"
          className={styles.phoneInput}
          placeholder={placeholder || (country.code === '+1' ? '(555) 123-4567' : '000 000 0000')}
          value={displayValue}
          onChange={handlePhoneNumberChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          required={required}
          autoComplete="tel-national"
          name="phone"
        />
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};

export default PhoneInput;